import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { FocusEvent, useCallback, useMemo } from 'react';
import { SubmitHandler, UseFormSetValue } from 'react-hook-form';

import useTicketDetailsPopup from './useTicketDetailsPopup';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { NoopT } from 'declarations/types/common';
import { SelectOptionT } from 'declarations/types/select';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useFetchClaimStatuses } from 'hooks/api/useClaimStatuses';
import { useFetchTicketPriorities } from 'hooks/api/useTicketPriorities';
import { useChangeTicketDetails } from 'hooks/api/useTickets';
import { useFetchTicketRequestSubcategories } from 'hooks/api/useTicketSubcategories';

export type ChangeTicketDetailsFormValuesT = {
  ticketId: string;
  amount: string;
  claimStatusId: number;
  claimSubmittedDate: Date | null;
  advisorNumber: string;
  techNumber: string;
  ticketPriorityId: number;
  subCategoryId: number | null;
};

type UseChangeTicketPriorityPopupT = {
  ticketPrioritiesOptions: SelectOptionT[];
  claimStatusesOptions: SelectOptionT[];
  ticketSubcategoriesOptions: SelectOptionT[];
  isLoadingTicketPrioritiesOptions: boolean;
  isLoadingClaimStatusesOptions: boolean;
  isLoadingTicketSubcategoriesOptions: boolean;
  isChangingTicketDetails: boolean;
  isTicketDetailsChanged: boolean;
  isTicketDetailsChangeError: boolean;
  changeTicketDetailsErrorMessage: string | undefined;
  changeTicketDetailsValidationErrorMessage: string | undefined;
  showPopupContent: boolean;
  handleAmountBlur: (
    setValue: UseFormSetValue<ChangeTicketDetailsFormValuesT>
  ) => (e: FocusEvent<HTMLInputElement>) => void;
  handleChangeTicketDetails: SubmitHandler<ChangeTicketDetailsFormValuesT>;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
};

const useChangeTicketDetailsForm = (): UseChangeTicketPriorityPopupT => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const { ticketDetails } = useTicketDetailsPopup();
  const roofTopId = ticketDetails?.roofTop.id || '';
  const claimStatusesRequestParams = {
    isEnabled: true,
    roofTopId: roofTopId,
  };
  const SubcategoryRequestParams = {
    isEnabled: true,
    roofTopId: ticketDetails?.roofTop.id || '',
  };
  /**
   * Api hooks
   */
  const ticketPrioritiesResult = useFetchTicketPriorities(true);

  const ticketSubcategoriesResult = useFetchTicketRequestSubcategories(SubcategoryRequestParams);

  const claimStatusesResult = useFetchClaimStatuses(claimStatusesRequestParams);

  const changeTicketDetailsMutation = useChangeTicketDetails({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
    },
  });

  /**
   * Variables
   */
  const ticketPrioritiesOptions = useMemo(
    () =>
      ticketPrioritiesResult.data
        ? ticketPrioritiesResult.data?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [ticketPrioritiesResult.data]
  );

  const ticketSubcategoriesOptions = useMemo(
    () =>
      ticketSubcategoriesResult.data
        ? ticketSubcategoriesResult.data?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [ticketSubcategoriesResult.data]
  );

  const changeTicketDetailsErrorMessage = useMemo(
    () => getErrorMessage(changeTicketDetailsMutation.error),
    [changeTicketDetailsMutation.error]
  );

  const changeTicketDetailsValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(changeTicketDetailsMutation.error),
    [changeTicketDetailsMutation.error]
  );

  const claimStatusesOptions = useMemo(
    () =>
      claimStatusesResult.data
        ? claimStatusesResult.data?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [claimStatusesResult.data]
  );

  /**
   * Handlers
   */
  const resetChangeTicketDetailsMutationState = useCallback(() => {
    if (changeTicketDetailsMutation.isSuccess || changeTicketDetailsMutation.isError) {
      changeTicketDetailsMutation.reset();
    }
  }, [changeTicketDetailsMutation]);

  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => () => {
      onClosePopupCb();
      resetChangeTicketDetailsMutationState();
    },
    [resetChangeTicketDetailsMutationState]
  );

  const handleChangeTicketDetails = useCallback(
    (data: ChangeTicketDetailsFormValuesT) => {
      changeTicketDetailsMutation.mutate({
        ...data,
        claimSubmittedDate: data.claimSubmittedDate
          ? format(data.claimSubmittedDate, 'yyyy-MM-dd')
          : null,
        subCategoryId: data.subCategoryId ? data.subCategoryId : null,
      });
    },
    [changeTicketDetailsMutation]
  );

  const setAmountValue = (
    value: string,
    setValue: UseFormSetValue<ChangeTicketDetailsFormValuesT>
  ) => {
    setValue('amount', value, { shouldValidate: true });
  };

  const handleAmountBlur = useCallback(
    (setValue: UseFormSetValue<ChangeTicketDetailsFormValuesT>) =>
      (e: FocusEvent<HTMLInputElement>) => {
        const { value } = e.target;
        const [numbersBeforeDot, numbersAfterDot] = value.split('.');
        const hasOneDecimal = !!numbersAfterDot && numbersAfterDot.length === 1;
        const hasTwoDecimal = !!numbersAfterDot && numbersAfterDot.length >= 2;

        if (!value) {
          setAmountValue('0.00', setValue);
        } else if (value && hasOneDecimal) {
          setAmountValue(`${numbersBeforeDot}.${numbersAfterDot}0`, setValue);
        } else if (value && !hasTwoDecimal) {
          setAmountValue(`${numbersBeforeDot}.00`, setValue);
        }
      },
    []
  );

  return {
    isLoadingTicketPrioritiesOptions: ticketPrioritiesResult.isLoading,
    isLoadingClaimStatusesOptions: claimStatusesResult.isLoading,
    isLoadingTicketSubcategoriesOptions: ticketSubcategoriesResult.isLoading,
    ticketPrioritiesOptions,
    claimStatusesOptions,
    ticketSubcategoriesOptions,
    isChangingTicketDetails: changeTicketDetailsMutation.isLoading,
    isTicketDetailsChanged: changeTicketDetailsMutation.isSuccess,
    isTicketDetailsChangeError: changeTicketDetailsMutation.isError,
    changeTicketDetailsErrorMessage,
    changeTicketDetailsValidationErrorMessage,
    showPopupContent: changeTicketDetailsMutation.isIdle || changeTicketDetailsMutation.isLoading,
    handleChangeTicketDetails,
    handleClosePopup,
    handleAmountBlur,
  };
};

export default useChangeTicketDetailsForm;
